<template>
    <Modal :title="$t('EDIT_MANAGER_SALESREP_PROFILE')">
        <template #content>
            <div class="modal" tabindex="-1" aria-modal="true" role="dialog">
                <div class="modal__header">
                    <p>{{ $t('EDIT_MANAGER_SALESREP_PROFILE') }}</p>
                </div>
                <div class="modal__actions">
                    <button
                            type="button"
                            class="btn btn-close"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                            @click="closeModal()">
                        <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="26"
                                height="25"
                                viewBox="0 0 26 25"
                                fill="none">
                            <path
                                    d="M2 23L23.9203 1.07969"
                                    stroke="white"
                                    stroke-width="3"></path>
                            <path
                                    d="M24 23L2.07969 1.07969"
                                    stroke="white"
                                    stroke-width="3"></path>
                        </svg>
                    </button>
                </div>
                <div class="modal__content grid gap-4 auto-rows-[58px]">
                    <ClientOnly>
                        <component :is="ProfileInfo" :is-manager-editing-his-sales-profile="true" />
                    </ClientOnly>
                </div>
            </div>
        </template>
    </Modal>
</template>
<script lang="ts">
import { type Ref } from 'vue'
import { useModal } from 'vue-final-modal'
import { useManagerStore } from '~/stores/managerStore'
import { useAuthStore } from '~/stores/authStore'
import ModalManagerProfileInvitationSuccess from '~/components/Modal/ModalManagerProfileInvitationSuccess.vue'
import { sleep, updatePhoto } from '~/utils'
import { UserRole } from '~/types/userRole'
import * as yup from 'yup'
import customErrorMessages from '~/locales/form/pl'
import RegExp from '~/utils/regExp'
import { ErrorMessage, Field, Form } from 'vee-validate'
import AvatarComponent from '~/components/shared/AvatarComponent.vue'
import ProfileInfo from '~/components/ProfileInfoForMenager.client.vue';
import Client from "../../layouts/client.vue";

export default {
    components: {Client, AvatarComponent, Field, Form, ErrorMessage },
    props: {
        userData: {
            type: Object,
            default: () => ({
                id: null,
                fullName: '',
                email: '',
                avatar: '',
                accountState: null,
            }),
        },
        type: {
            type: String,
            default: 'offers',
        },
    },
    emits: ['update:modelValue', 'closed', 'opened'],
    setup(props, ctx) {
        const { t: $t } = useI18n()
        const { sendSalesRepInvitation } = useAuthStore()
        const { $toast } = useNuxtApp()
        const { userData } = toRefs(props)
        const { userInfo } = storeToRefs(useUserStore())

        const companyAvatar: any = computed(() => {
            return userInfo.value?.companyData?.image
        })

        const avatarImage: Ref<string | null> = ref(userData.value.avatar)
        const fullName: Ref<string> = ref(userData.value.fullName)
        const email: Ref<string> = ref(userData.value.email)
        const phone: Ref<string> = ref('')

        const store = useManagerStore()

        function closeModal() {
            ctx.emit('closed')
        }

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        function deleteProduct({ id, type }: { id: number | null; type: string }) {
            if (!id) return

            store.removeItem({ id, type })
        }

        async function sendInvitation() {
            const inivitationResponse: any = await sendSalesRepInvitation(
                UserRole.SALES_REP,
                {
                    firstName: fullName.value.split(' ')[0] ?? '',
                    lastName: fullName.value.split(' ')[1] ?? '',
                    phoneNumber: phone.value,
                    email: email.value,
                }
            )

            if (inivitationResponse.status === 'error') {
                throw new Error($t('FORM_ERRORS.INVITATION_FAILED'))
            }

            return inivitationResponse
        }

        function updateProfilePhoto(event: Event) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            updatePhoto(event).then((result: string) => {
                nextTick(() => {
                    avatarImage.value = result
                })
            })
        }
        function save() {
            sendInvitation()
                .then(() => {
                    const { open: openSuccessModal } = useModal({
                        component: ModalManagerProfileInvitationSuccess,
                    })
                    ctx.emit('closed')

                    openSuccessModal()
                })
                .catch(e => {
                    ctx.emit('closed')
                    $toast.error(e.message)
                })
        }

        const newMemberSchema = yup.object({
            fullName: yup.string().required(customErrorMessages.required),
            phone: yup
                .string()
                .required(customErrorMessages.required)
                .matches(RegExp.phone, customErrorMessages.phone),
            email: yup
                .string()
                .required(customErrorMessages.required)
                .matches(RegExp.email, customErrorMessages.email),
        })

        return {
            ProfileInfo,
            newMemberSchema,
            avatarImage,
            userInfo,
            fullName,
            email,
            phone,
            companyAvatar,
            sendInvitation,
            closeModal,
            updateProfilePhoto,
            save,
        }
    },
    computed: {
        UserRole() {
            return UserRole
        },
    },
}
</script>
<style lang="scss" scoped>
.modal {
  grid-template-rows: max-content minmax(500px, 1fr);
  grid-template-columns: 212px 350px 50px;
  grid-template-areas:
    'header . actions'
    'content content content';
  &__content {
    overflow: auto;
  }
}
@media (max-width: 1496px) {
  .modal {
    grid-template-rows: max-content minmax(500px, 1fr);
  }
}
@media (max-width: 1240px) {
  .modal {
    grid-template-columns: 212px 350px 50px;
  }
}
@media (max-width: 1024px) {
  .modal {
    height: calc(350px - 1em);
  }
}
</style>
